// frontend/src/components/modals/ConfirmationModal.jsx
import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';

/**
 * 🛡️ MODAL DE CONFIRMAÇÃO REUTILIZÁVEL PARA AÇÕES CRÍTICAS
 * 
 * Este componente cria modais de confirmação elegantes e seguros
 * para ações que podem causar perda de dados ou mudanças irreversíveis.
 */

const ConfirmationModal = ({
  show,
  onHide,
  onConfirm,
  title = "Confirmar Ação",
  message = "Tem certeza que deseja continuar?",
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  type = "danger", // danger, warning, info, success
  icon = null,
  requireTyping = false,
  confirmationText = "",
  loading = false,
  size = "md", // sm, md, lg, xl
  showCheckbox = false,
  checkboxText = "Compreendo que esta ação não pode ser desfeita",
  additionalInfo = null,
  children = null
}) => {
  const [typedText, setTypedText] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Configurações visuais por tipo
  const typeConfig = {
    danger: {
      iconColor: 'text-danger',
      defaultIcon: 'mdi:alert-circle',
      buttonVariant: 'danger',
      headerBg: 'bg-danger-subtle',
      borderColor: 'border-danger'
    },
    warning: {
      iconColor: 'text-warning',
      defaultIcon: 'mdi:alert',
      buttonVariant: 'warning',
      headerBg: 'bg-warning-subtle',
      borderColor: 'border-warning'
    },
    info: {
      iconColor: 'text-info',
      defaultIcon: 'mdi:information',
      buttonVariant: 'info',
      headerBg: 'bg-info-subtle',
      borderColor: 'border-info'
    },
    success: {
      iconColor: 'text-success',
      defaultIcon: 'mdi:check-circle',
      buttonVariant: 'success',
      headerBg: 'bg-success-subtle',
      borderColor: 'border-success'
    }
  };

  const config = typeConfig[type] || typeConfig.danger;
  const modalIcon = icon || config.defaultIcon;

  // Verificar se pode confirmar
  const canConfirm = () => {
    let canProceed = true;
    
    if (requireTyping && confirmationText) {
      canProceed = canProceed && (typedText.trim().toLowerCase() === confirmationText.toLowerCase());
    }
    
    if (showCheckbox) {
      canProceed = canProceed && isChecked;
    }
    
    return canProceed && !loading && !isProcessing;
  };

  // Lidar com confirmação
  const handleConfirm = async () => {
    if (!canConfirm()) return;
    
    try {
      setIsProcessing(true);
      await onConfirm();
      handleClose(); // Fechar modal após sucesso
    } catch (error) {
      console.error('Erro na confirmação:', error);
      // O modal permanece aberto em caso de erro
    } finally {
      setIsProcessing(false);
    }
  };

  // Lidar com fechamento
  const handleClose = () => {
    if (isProcessing || loading) return; // Não fechar durante processamento
    
    setTypedText('');
    setIsChecked(false);
    setIsProcessing(false);
    onHide();
  };

  // Lidar com tecla Enter
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && canConfirm()) {
      e.preventDefault();
      handleConfirm();
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      backdrop="static" // Não fechar clicando fora durante processamento
      keyboard={!isProcessing} // Não fechar com ESC durante processamento
      size={size}
      centered
    >
      <Modal.Header 
        closeButton={!isProcessing && !loading} 
        className={`${config.headerBg} ${config.borderColor} border-bottom-0`}
      >
        <Modal.Title className="d-flex align-items-center">
          <Icon 
            icon={modalIcon} 
            className={`${config.iconColor} me-2`} 
            style={{ fontSize: '1.5rem' }} 
          />
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="py-4">
        {/* Mensagem principal */}
        <div className="mb-3">
          <p className="mb-0 lead">{message}</p>
        </div>

        {/* Informações adicionais */}
        {additionalInfo && (
          <div className="alert alert-light border mb-3">
            {additionalInfo}
          </div>
        )}

        {/* Conteúdo customizado */}
        {children && (
          <div className="mb-3">
            {children}
          </div>
        )}

        {/* Campo de digitação para confirmação */}
        {requireTyping && confirmationText && (
          <div className="mb-3">
            <label className="form-label small text-muted">
              Para confirmar, digite: <strong>{confirmationText}</strong>
            </label>
            <input
              type="text"
              className="form-control"
              value={typedText}
              onChange={(e) => setTypedText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={`Digite "${confirmationText}" para confirmar`}
              disabled={isProcessing || loading}
              autoFocus={requireTyping}
            />
          </div>
        )}

        {/* Checkbox de confirmação */}
        {showCheckbox && (
          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="confirmationCheckbox"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              disabled={isProcessing || loading}
            />
            <label className="form-check-label small text-muted" htmlFor="confirmationCheckbox">
              {checkboxText}
            </label>
          </div>
        )}

        {/* Loading indicator */}
        {(isProcessing || loading) && (
          <div className="text-center mb-3">
            <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
              <span className="visually-hidden">Processando...</span>
            </div>
            <span className="text-muted">Processando...</span>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-light border-top">
        <div className="d-flex w-100 gap-2">
          <Button
            variant="outline-secondary"
            onClick={handleClose}
            disabled={isProcessing || loading}
            className="flex-fill"
          >
            <Icon icon="mdi:close" className="me-1" />
            {cancelText}
          </Button>
          <Button
            variant={config.buttonVariant}
            onClick={handleConfirm}
            disabled={!canConfirm()}
            className="flex-fill"
          >
            {(isProcessing || loading) ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Carregando...</span>
                </div>
                Processando...
              </>
            ) : (
              <>
                <Icon icon="mdi:check" className="me-1" />
                {confirmText}
              </>
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmationModal;



























